import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Calendar, Gift, Info, Copy, CheckCircle, Sparkles, TrendingUp, DollarSign, Target } from 'lucide-react';
import { useScreenSize, useToast } from '@/hooks';
import { activateGiftCard } from '@/services/giftCard/giftCardService';
import styles from './GiftCardViewModal.module.css';

/**
 * GiftCardViewModal Component
 * Comprehensive modal displaying all gift card details with progress level
 */
const GiftCardViewModal = ({ isOpen, onClose, card, onActivate }) => {
  const { isMobile } = useScreenSize();
  const { showSuccess, showError } = useToast();
  const [copied, setCopied] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  if (!card) return null;

  const activationCode = card.activationCode || card.uniqueKey || `GFT${card.cardId?.padStart(5, '0') || '00000'}`;
  const isActivated = card.isActivated || card.status === 'activated' || card.status === 'Activated';
  
  // Calculate progress percentage
  const totalWagerRequired = card.requiredWagerUSD || card.totalWager || 0;
  const currentWager = card.currentWager || 0;
  const progressPercentage = totalWagerRequired > 0 
    ? Math.min((currentWager / totalWagerRequired) * 100, 100) 
    : 0;

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(activationCode);
      setCopied(true);
      showSuccess('Gift card code copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      showSuccess('Failed to copy code');
    }
  };

  const handleActivate = async () => {
    if (!activationCode) {
      showError('Activation code is missing');
      return;
    }

    setIsActivating(true);
    try {
      const response = await activateGiftCard(activationCode);
      
      if (response.success) {
        showSuccess('Gift card activated successfully!');
        if (onActivate) {
          onActivate(activationCode);
        }
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        showError(response.message || 'Failed to activate gift card');
      }
    } catch (error) {
      // Extract error message from API response
      let errorMessage = 'Failed to activate gift card. Please try again.';
      
      if (error.response?.data) {
        // Check for error field first (e.g., {"error":"Gift card not activated by user"})
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } 
        // Check for message field
        else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        // Check for error in nested structure
        else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showError(errorMessage);
    } finally {
      setIsActivating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={`${styles.modal} ${isMobile ? styles.mobile : ''}`}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <Gift size={24} className={styles.headerIcon} />
              <h2 className={styles.title}>Gift Card View</h2>
            </div>
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className={styles.content}>
            {/* Card Information Section */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Card Information</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Card Name</span>
                  <span className={styles.infoValue}>{card.title || card.description || 'Gift Card'}</span>
                </div>

                {card.amount > 0 && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>
                      <DollarSign size={16} className={styles.icon} />
                      Amount
                    </span>
                    <span className={styles.amountValue}>${card.amount.toLocaleString('en-US')}</span>
                  </div>
                )}

                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Activation Code</span>
                  <div className={styles.activationCodeContainer}>
                    <span className={styles.infoValue}>{activationCode}</span>
                    <button
                      className={styles.copyButton}
                      onClick={handleCopyCode}
                      aria-label="Copy activation code"
                      title="Copy code"
                    >
                      {copied ? (
                        <CheckCircle size={18} className={styles.copyIcon} />
                      ) : (
                        <Copy size={18} className={styles.copyIcon} />
                      )}
                    </button>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Status</span>
                  <span className={`${styles.statusValue} ${
                    card.status === 'claimed' 
                      ? styles.statusClaimed 
                      : card.isActivated 
                        ? styles.statusActivated 
                        : styles.statusUnclaimed
                  }`}>
                    {card.status === 'claimed' 
                      ? 'Claimed' 
                      : card.isActivated 
                        ? 'Activated' 
                        : 'Not Activated'}
                  </span>
                </div>

                {card.expiryDate && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>
                      <Calendar size={16} className={styles.icon} />
                      Expiry Date
                    </span>
                    <span className={styles.infoValue}>{formatDate(card.expiryDate)}</span>
                  </div>
                )}
                
                {card.periodDays && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>
                      <Calendar size={16} className={styles.icon} />
                      Valid Period
                    </span>
                    <span className={styles.infoValue}>{card.periodDays} days</span>
                  </div>
                )}

                {card.activatedDate && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>
                      <Calendar size={16} className={styles.icon} />
                      Activated Date
                    </span>
                    <span className={styles.infoValue}>{formatDate(card.activatedDate)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Requirements Section */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <Info size={20} className={styles.sectionIcon} />
                <h3 className={styles.sectionTitle}>Requirements</h3>
              </div>

              <div className={styles.requirementsGrid}>
                {/* Deposit Requirement */}
                <div className={styles.requirementCard}>
                  <div className={styles.requirementHeader}>
                    <span className={styles.requirementIcon}>💰</span>
                    <span className={styles.requirementTitle}>Deposit Requirement</span>
                  </div>
                  <div className={styles.requirementValue}>
                    {card.requiresDeposit 
                      ? `$${card.depositAmount?.toLocaleString('en-US') || 0} minimum` 
                      : 'No deposit required'}
                  </div>
                  {card.depositStatus !== undefined && (
                    <div className={styles.requirementStatus}>
                      Status: {card.depositStatus ? '✓ Met' : '✗ Not Met'}
                    </div>
                  )}
                </div>

                {/* Wager Requirement */}
                <div className={styles.requirementCard}>
                  <div className={styles.requirementHeader}>
                    <Target size={20} className={styles.requirementIconSvg} />
                    <span className={styles.requirementTitle}>Wager Requirement</span>
                  </div>
                  <div className={styles.requirementValue}>
                    {card.wagerRequirement !== undefined && card.wagerRequirement > 0 
                      ? `${card.wagerRequirement}x ($${(card.requiredWagerUSD || card.totalWager || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total)` 
                      : (card.requiredWagerUSD > 0 
                        ? `$${(card.requiredWagerUSD).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total`
                        : 'No wager required')}
                  </div>
                  {card.wagerStatus !== undefined && (
                    <div className={styles.requirementStatus}>
                      Status: {card.wagerStatus ? '✓ Complete' : '✗ In Progress'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Progress Level Section */}
            {isActivated && (totalWagerRequired > 0 || card.requiredWagerUSD > 0) && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <TrendingUp size={20} className={styles.sectionIcon} />
                  <h3 className={styles.sectionTitle}>Wager Progress</h3>
                </div>
                
                <div className={styles.progressContainer}>
                  <div className={styles.progressHeader}>
                    <div className={styles.progressInfo}>
                      <span className={styles.progressLabel}>Current Progress</span>
                      <span className={styles.progressPercentage}>{progressPercentage.toFixed(1)}%</span>
                    </div>
                    <div className={styles.progressAmounts}>
                      <span className={styles.progressCurrent}>
                        ${currentWager.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className={styles.progressSeparator}>/</span>
                      <span className={styles.progressTotal}>
                        ${totalWagerRequired.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  
                  <div className={styles.progressBarContainer}>
                    <div 
                      className={styles.progressBar}
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                  
                  <div className={styles.progressRemaining}>
                    <span>Remaining: ${(totalWagerRequired - currentWager).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Description Section */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <Info size={20} className={styles.sectionIcon} />
                <h3 className={styles.sectionTitle}>Description</h3>
              </div>
              <p className={styles.descriptionText}>{card.description || 'No description available'}</p>
            </div>

            {/* Activate Button - Only show if not activated */}
            {!isActivated && (
              <div className={styles.activateSection}>
                <button
                  className={styles.activateButton}
                  onClick={handleActivate}
                  disabled={isActivating}
                  aria-label="Activate gift card"
                >
                  <Sparkles size={18} className={styles.activateIcon} />
                  <span>{isActivating ? 'Activating...' : 'Activate Gift Card'}</span>
                </button>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default GiftCardViewModal;
